'use client';

import { useTranslation } from 'next-i18next';
import { LinkStyled } from '@components/common/Anchor/LinkStyled';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useMcHomeGreeting } from '@hooks/features/useMcHomeGreeting';
import { useHomeJobsSlot } from '@hooks/features/useHomeJobsSlot';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';
import { HomeJobsSlot } from '@components/templates/homepage/HomeJobsSlot';

interface CandidateLandingProps {
  locale: Locale;
  isAuthenticated?: boolean;
}

/**
 * Empty-state landing for the candidate MC views.
 * Renders the My Bestie CTA and the rotating greeting headline above the
 * (initially empty) results area. The CandidatePromptBar lives below this
 * component in the page layout (the shell's fixed bar).
 */
export const CandidateLanding = ({ locale, isAuthenticated = false }: CandidateLandingProps) => {
  const { t } = useTranslation('common');

  // Resolve the MC context the same way the shell does: explicit `mc_context` cookie wins,
  // otherwise an authenticated company account (hasBusinessWallet) defaults to `company`.
  // The greeting, the home slot and the My Bestie CTA all key off this single value.
  const hasBusinessWallet = !!useAppSelector(getUserHeaderInfo)?.hasBusinessWallet;
  const fallbackContext = isAuthenticated && hasBusinessWallet ? 'company' : 'candidate';
  const { currentContext } = useMissionControlContext({ fallbackContext });
  const isCompanyContext = currentContext === 'company';

  const { message: greetingMessage } = useMcHomeGreeting(isCompanyContext ? 'employer' : 'candidate');

  // Single owner of the home-slot state. <HomeJobsSlot> receives it directly; the SWR keys
  // inside dedupe, so there is no extra request. Gates the "see all jobs" link below.
  const slotState = useHomeJobsSlot(locale, isAuthenticated, currentContext);
  const showSeeAllJobs = slotState.variant === 'talent' && slotState.hasItems;
  // Company context (own jobs surface) has its own "Gestionare candidați" link — the
  // candidate "activate My Bestie" CTA does not belong there, jobs or not.
  const showMyBestieCta = !isCompanyContext;

  return (
    <div className="flex grow flex-col items-center justify-end px-4 pb-16 pt-8 md:pb-12">
      <div className="flex w-full max-w-4xl flex-col gap-4">
        <span className="mb-12 min-h-[1.375em] w-full text-balance text-center text-3xl font-bold leading-snug sm:text-4xl lg:text-[56px] lg:leading-tight">
          {greetingMessage}
        </span>
      </div>

      <div className="container mx-auto mt-4">
        <HomeJobsSlot locale={locale} state={slotState} unwrapped />

        <div className="mx-auto mt-4 flex w-full max-w-2xl flex-col items-center justify-center gap-3 pt-6 text-base text-ink md:text-lg">
          {showSeeAllJobs && (
            <LinkStyled href={getI18nLinkHref('/mc/job/list', locale)} styling="none" className="text-center underline underline-offset-4">
              {t('home.btn.label.see-jobs')}
            </LinkStyled>
          )}

          {showMyBestieCta && (
            <LinkStyled href="/mc/my-bestie" styling="none" className="text-center underline underline-offset-4">
              {t('my.bestie.home.cta.short')}
            </LinkStyled>
          )}
        </div>
      </div>
    </div>
  );
};
